import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import { IconeChevronBaixo, IconeFechar } from '../icones'
import { ATRIBUTO_VIEWPORT_3D } from '../workspace/Workspace'

export interface GrupoFerramentas {
  id: string
  rotulo: string
  /**
   * Preguiçoso de propósito. Como `ReactNode`, o conteúdo de TODOS os grupos era
   * construído a cada render — um `<PainelExtracao />` inteiro por grupo fechado.
   * Como função, só o grupo aberto monta.
   */
  conteudo: () => ReactNode
  /** Ação persistente do módulo; quando definida, o botão não abre bandeja. */
  aoAtivar?: () => void
  ativa?: boolean
  /** Reforço visual do rótulo; nunca substitui o texto no desktop. */
  icone?: ReactNode
  resumo?: string
  badge?: ReactNode
  desabilitado?: boolean
  /**
   * Rótulo para o dock do celular quando há muitos grupos dividindo a largura
   * (cada um fica com ~45 px e "Importar/Exportar" vira "Imp…").
   */
  rotuloCurto?: string
  /** Fora do dock do celular: o conteúdo já está à mão por outro caminho lá. */
  somenteDesktop?: boolean
}

export type TomChipFerramenta = 'neutro' | 'info' | 'sucesso' | 'alerta' | 'perigo'

export interface ChipFerramenta {
  id: string
  rotulo: string
  valor?: ReactNode
  tom?: TomChipFerramenta
}

export interface BarraFerramentasCenarioProps {
  grupos: readonly GrupoFerramentas[]
  grupoAtivo: string | null
  aoMudarGrupo: (id: string | null) => void
  titulo?: string
  identificadorCenario?: ReactNode
  chips?: readonly ChipFerramenta[]
  acoesRapidas?: ReactNode
  /** Fecha a bandeja quando o módulo/cenário identificado por esta chave muda. */
  chaveContexto?: string
  className?: string
}

/** Folga para não acender a seta por causa de arredondamento de subpixel. */
const FOLGA_ROLAGEM = 4

export interface EstadoRolagem {
  inicio: boolean
  fim: boolean
}

/**
 * Quais lados da faixa ainda têm ferramenta escondida. É o que decide as setas
 * de rolagem: no celular a faixa não cabe em nenhuma tela e, sem elas, os
 * grupos à direita ficavam inalcançáveis.
 */
export function estadoDeRolagem(
  scrollLeft: number,
  scrollWidth: number,
  clientWidth: number,
): EstadoRolagem {
  const sobra = scrollWidth - clientWidth
  if (sobra <= FOLGA_ROLAGEM) return { inicio: false, fim: false }
  return {
    inicio: scrollLeft > FOLGA_ROLAGEM,
    fim: scrollLeft < sobra - FOLGA_ROLAGEM,
  }
}

export function alternarGrupoFerramentas(
  grupoAtivo: string | null,
  grupoAcionado: string,
): string | null {
  return grupoAtivo === grupoAcionado ? null : grupoAcionado
}

function identificadorSeguro(valor: string): string {
  return valor.trim().toLocaleLowerCase('pt-BR').replace(/[^a-z0-9_-]+/g, '-')
}

export function ResumoCenario({ chips }: { chips: readonly ChipFerramenta[] }) {
  if (chips.length === 0) return null

  return (
    <div className="ribbon-chips" aria-label="Resumo do cenário">
      {chips.map((chip) => (
        <output
          className={`ribbon-chip ribbon-chip--${chip.tom ?? 'neutro'}`}
          key={chip.id}
          aria-label={`${chip.rotulo}${chip.valor == null ? '' : `: ${String(chip.valor)}`}`}
        >
          <span className="ribbon-chip__rotulo">{chip.rotulo}</span>
          {chip.valor != null && <strong>{chip.valor}</strong>}
        </output>
      ))}
    </div>
  )
}

export function BandejaFerramentas({
  grupo,
  id,
  aoFechar,
  style,
}: {
  grupo: GrupoFerramentas
  id: string
  aoFechar?: () => void
  style?: React.CSSProperties
}) {
  return (
    <section
      id={id}
      className="ribbon-bandeja ribbon-popover"
      role="region"
      aria-label={`Opções de ${grupo.rotulo}`}
      style={style}
    >
      <header className="ribbon-popover__topo">
        <div className="ribbon-popover__cabecalho">
          <strong className="ribbon-popover__titulo">{grupo.rotulo}</strong>
          {grupo.resumo && <p className="ribbon-bandeja__resumo">{grupo.resumo}</p>}
        </div>
        {aoFechar && (
          <button
            type="button"
            className="ribbon-popover__fechar"
            onClick={aoFechar}
            aria-label={`Fechar ${grupo.rotulo}`}
            title="Fechar (Esc)"
          >
            <IconeFechar />
          </button>
        )}
      </header>
      <div className="ribbon-bandeja__conteudo">{grupo.conteudo()}</div>
    </section>
  )
}

export function BarraFerramentasCenario({
  grupos,
  grupoAtivo,
  aoMudarGrupo,
  titulo = 'Ferramentas do cenário',
  identificadorCenario,
  chips = [],
  acoesRapidas,
  chaveContexto,
  className = '',
}: BarraFerramentasCenarioProps) {
  const raizRef = useRef<HTMLDivElement>(null)
  const barraRef = useRef<HTMLDivElement>(null)
  const botoesRef = useRef<Array<HTMLButtonElement | null>>([])
  const contextoAnteriorRef = useRef(chaveContexto)
  const grupoAnteriorRef = useRef(grupoAtivo)
  const instancia = identificadorSeguro(useId())
  const grupoAberto = grupos.find((grupo) => grupo.id === grupoAtivo) ?? null
  const idBandeja = grupoAberto
    ? `bandeja-ferramentas-${identificadorSeguro(grupoAberto.id)}`
    : undefined

  const [popoverX, setPopoverX] = useState<number | undefined>(undefined)
  const [rolagem, setRolagem] = useState<EstadoRolagem>({ inicio: false, fim: false })

  // A faixa rola na horizontal, mas no toque o arrasto é pouco descoberto e
  // em parte dos navegadores móveis nem chega ao container. As setas garantem
  // o acesso a todos os grupos com um toque.
  useEffect(() => {
    const barra = barraRef.current
    if (!barra) return

    const medir = () => {
      setRolagem((atual) => {
        const novo = estadoDeRolagem(barra.scrollLeft, barra.scrollWidth, barra.clientWidth)
        return novo.inicio === atual.inicio && novo.fim === atual.fim ? atual : novo
      })
    }

    medir()
    barra.addEventListener('scroll', medir, { passive: true })
    let observador: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined') {
      observador = new ResizeObserver(medir)
      observador.observe(barra)
      for (const filho of Array.from(barra.children)) observador.observe(filho)
    }
    return () => {
      barra.removeEventListener('scroll', medir)
      observador?.disconnect()
    }
  }, [grupos, chips, acoesRapidas])

  const rolarFaixa = (direcao: -1 | 1) => {
    const barra = barraRef.current
    if (!barra) return
    barra.scrollBy({ left: direcao * barra.clientWidth * 0.8, behavior: 'smooth' })
  }

  useEffect(() => {
    if (!grupoAberto || !raizRef.current) {
      setPopoverX(undefined)
      return
    }
    const indice = grupos.findIndex((g) => g.id === grupoAberto.id)
    const botao = botoesRef.current[indice]
    if (botao && raizRef.current) {
      const raizRect = raizRef.current.getBoundingClientRect()
      const botaoRect = botao.getBoundingClientRect()
      const relativoX = Math.max(8, botaoRect.left - raizRect.left)
      // A largura real do popover (max-content, até 560 px): supor 380 deixava
      // um popover largo (ex.: Cena do Table top) passar da borda direita em
      // telas médias, com o botão de fechar fora da tela.
      const largura = (idBandeja ? document.getElementById(idBandeja)?.offsetWidth : 0) || 380
      const maxX = Math.max(8, raizRect.width - largura - 8)
      setPopoverX(Math.min(relativoX, maxX))
    }
  }, [grupoAberto, grupos, idBandeja])

  useEffect(() => {
    const fecharAoClicarFora = (evento: PointerEvent) => {
      const alvo = evento.target instanceof Element ? evento.target : null
      if (!raizRef.current || raizRef.current.contains(alvo)) return
      // A cena é o objeto de trabalho do painel, não um "fora" dele: acionar um
      // marcador 3D não pode apagar o progresso e o resultado que o painel está
      // mostrando naquele mesmo gesto.
      if (alvo?.closest(`[${ATRIBUTO_VIEWPORT_3D}]`)) return
      aoMudarGrupo(null)
    }
    const fecharComEscape = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') aoMudarGrupo(null)
    }

    document.addEventListener('pointerdown', fecharAoClicarFora)
    document.addEventListener('keydown', fecharComEscape)
    return () => {
      document.removeEventListener('pointerdown', fecharAoClicarFora)
      document.removeEventListener('keydown', fecharComEscape)
    }
  }, [aoMudarGrupo])

  useEffect(() => {
    if (contextoAnteriorRef.current !== chaveContexto) {
      contextoAnteriorRef.current = chaveContexto
      if (grupoAtivo && grupoAtivo === grupoAnteriorRef.current) {
        aoMudarGrupo(null)
      }
    }
    grupoAnteriorRef.current = grupoAtivo
  }, [aoMudarGrupo, chaveContexto, grupoAtivo])

  const navegarComTeclado = (evento: ReactKeyboardEvent<HTMLButtonElement>, indice: number) => {
    const teclas = ['ArrowLeft', 'ArrowRight', 'Home', 'End']
    if (!teclas.includes(evento.key)) return

    evento.preventDefault()
    const habilitados = grupos
      .map((grupo, posicao) => (grupo.desabilitado ? -1 : posicao))
      .filter((posicao) => posicao >= 0)
    const posicaoAtual = habilitados.indexOf(indice)
    const destino = evento.key === 'Home'
      ? habilitados[0]
      : evento.key === 'End'
        ? habilitados.at(-1)
        : habilitados[(posicaoAtual + (evento.key === 'ArrowRight' ? 1 : -1) + habilitados.length) % habilitados.length]

    if (destino != null) botoesRef.current[destino]?.focus()
  }

  return (
    <div
      ref={raizRef}
      className={`ribbon-cenario ${className ?? ''}`.trim()}
      data-ribbon-instancia={instancia}
    >
      <div className="ribbon-cenario__barra" ref={barraRef}>
        {identificadorCenario && (
          <div className="ribbon-identificador-cenario">
            {identificadorCenario}
          </div>
        )}
        <div className="ribbon-grupos" role="toolbar" aria-label={titulo}>
          {grupos.map((grupo, indice) => {
            const aberto = grupo.id === grupoAberto?.id
            const ativo = grupo.ativa ?? aberto
            const bandejaDoGrupo = `bandeja-ferramentas-${identificadorSeguro(grupo.id)}`
            return (
              <button
                ref={(elemento) => { botoesRef.current[indice] = elemento }}
                type="button"
                className={`ribbon-grupo${ativo ? ' ribbon-grupo--ativo' : ''}`}
                key={grupo.id}
                aria-expanded={grupo.aoAtivar ? undefined : aberto}
                aria-controls={aberto ? bandejaDoGrupo : undefined}
                disabled={grupo.desabilitado}
                onClick={() => grupo.aoAtivar ? grupo.aoAtivar() : aoMudarGrupo(alternarGrupoFerramentas(grupoAtivo, grupo.id))}
                onKeyDown={(evento) => navegarComTeclado(evento, indice)}
              >
                {grupo.icone}
                <span>{grupo.rotulo}</span>
                {!grupo.aoAtivar && <span className={`ribbon-grupo__seta ${aberto ? 'aberta' : ''}`}>
                  <IconeChevronBaixo tamanho={13} />
                </span>}
                {grupo.badge != null && <span className="ribbon-grupo__badge">{grupo.badge}</span>}
              </button>
            )
          })}
        </div>
        <ResumoCenario chips={chips} />
        {acoesRapidas && (
          <div className="ribbon-acoes-rapidas" aria-label="Ações rápidas">
            {acoesRapidas}
          </div>
        )}
      </div>

      {rolagem.inicio && (
        <button
          type="button"
          className="ribbon-rolagem ribbon-rolagem--inicio"
          onClick={() => rolarFaixa(-1)}
          aria-hidden="true"
          tabIndex={-1}
          title="Ver ferramentas anteriores"
        >
          <SetaRolagem direcao="inicio" />
        </button>
      )}
      {rolagem.fim && (
        <button
          type="button"
          className="ribbon-rolagem ribbon-rolagem--fim"
          onClick={() => rolarFaixa(1)}
          aria-hidden="true"
          tabIndex={-1}
          title="Ver mais ferramentas"
        >
          <SetaRolagem direcao="fim" />
        </button>
      )}

      {grupoAberto && idBandeja && (
        <BandejaFerramentas
          grupo={grupoAberto}
          id={idBandeja}
          aoFechar={() => aoMudarGrupo(null)}
          style={popoverX != null ? { left: `${popoverX}px` } : undefined}
        />
      )}
    </div>
  )
}

function SetaRolagem({ direcao }: { direcao: 'inicio' | 'fim' }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        d={direcao === 'fim'
          ? 'M7.21 4.23a.75.75 0 011.06-.02l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 11-1.04-1.08L11.17 10 7.19 6.29a.75.75 0 01.02-1.06z'
          : 'M12.79 4.23a.75.75 0 01.02 1.06L8.83 10l3.98 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z'}
        clipRule="evenodd"
      />
    </svg>
  )
}
